import { Check, X } from "lucide-react";
import {
  testgreenBackground,
  testgreenText,
  testRedBackground,
  testRedText,
} from "../../constants/test";
import styles from "./dloscePrescriptionAnswerBlock.module.scss";

export type DlosceClassificationOption = { value: string; label: string };
export type DlosceRegimenOption = {
  regimen_id: number;
  display: string;
  is_correct?: boolean;
};
export type DlosceMedOption = {
  med_id: number;
  label: string;
  drug: string;
  is_otc: boolean;
  classification?: string;
  explanation?: string | null;
  regimens: DlosceRegimenOption[];
};
export type DlosceTask = {
  classifications: DlosceClassificationOption[];
  medications: DlosceMedOption[];
};
export type DlosceMedAnswer = {
  med_id: number;
  classification: string | null;
  regimen_id: number | null;
};

// Same list and order as DlosceRx.ClassificationOptions on the server,
// only used if the task somehow arrives without it
const FALLBACK_CLASSIFICATIONS: DlosceClassificationOption[] = [
  { value: "first_line", label: "First-line therapy" },
  { value: "acceptable", label: "Acceptable, but non-preferred" },
  { value: "contraindicated", label: "Contraindicated" },
];

const sameClassification = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

type Props = {
  dlosceTask: DlosceTask | undefined;
  examinationState: any;
  setExaminationState?: (updater: (prev: any) => any) => void;
  currentQuestionId: number | string;
  readOnly?: boolean; // review / answer revealed
};

function DloscePrescriptionAnswerBlock({
  dlosceTask,
  examinationState,
  setExaminationState,
  currentQuestionId,
  readOnly,
}: Props) {
  const medications = dlosceTask?.medications ?? [];
  const classifications =
    dlosceTask?.classifications?.length > 0
      ? dlosceTask.classifications
      : FALLBACK_CLASSIFICATIONS;

  const answers: DlosceMedAnswer[] =
    examinationState?.dloscePrescriptionsById?.[currentQuestionId] ?? [];
  const answerFor = (medId: number) => answers.find((a) => a.med_id === medId);

  const answerViewed =
    examinationState?.tutorialMode?.includes(currentQuestionId) ?? false;
  const showAnswers = !!readOnly || answerViewed;
  const locked = showAnswers || !setExaminationState;

  const updateAnswer = (medId: number, patch: Partial<DlosceMedAnswer>) => {
    if (locked) return;
    setExaminationState((prev) => {
      const current: DlosceMedAnswer[] =
        prev.dloscePrescriptionsById?.[currentQuestionId] ?? [];
      // One entry per medication, in task order
      const next = medications.map((med) => {
        const existing = current.find((a) => a.med_id === med.med_id) ?? {
          med_id: med.med_id,
          classification: null,
          regimen_id: null,
        };
        return med.med_id === medId ? { ...existing, ...patch } : existing;
      });
      return {
        ...prev,
        dloscePrescriptionsById: {
          ...prev.dloscePrescriptionsById,
          [currentQuestionId]: next,
        },
      };
    });
  };

  if (medications.length === 0) {
    return (
      <div className={styles.block}>
        <span className={styles.instructions}>
          This prescription task is not available.
        </span>
      </div>
    );
  }

  //
  //Answer viewed / review
  //
  if (showAnswers) {
    const results = medications.map((med) => {
      const answer = answerFor(med.med_id);
      const classificationCorrect = sameClassification(
        answer?.classification,
        med.classification,
      );
      const regimenCorrect =
        answer?.regimen_id != null &&
        med.regimens.some(
          (r) => r.regimen_id === answer.regimen_id && r.is_correct,
        );
      return { med, answer, classificationCorrect, regimenCorrect };
    });
    return (
      <div className={styles.block}>
        {results.map(
          ({ med, answer, classificationCorrect, regimenCorrect }) => (
            <div key={med.med_id} className={styles.resultCard}>
              <div className={styles.resultHeader}>
                <span className={styles.resultDrug}>{med.drug}</span>
              </div>

              <div className={styles.resultBody}>
                <OptionList
                  title="Treatment Classification"
                  answered={!!answer?.classification}
                  correct={classificationCorrect}
                  options={classifications.map((c) => ({
                    key: c.value,
                    text: c.label,
                    isCorrect: sameClassification(c.value, med.classification),
                    isPicked: sameClassification(
                      c.value,
                      answer?.classification,
                    ),
                  }))}
                />
                <OptionList
                  title="Medication Regimen"
                  answered={answer?.regimen_id != null}
                  correct={regimenCorrect}
                  options={med.regimens.map((r) => ({
                    key: String(r.regimen_id),
                    text: r.display,
                    isCorrect: !!r.is_correct,
                    isPicked: answer?.regimen_id === r.regimen_id,
                  }))}
                />
              </div>

              {med.explanation && (
                <div className={styles.explanation}>
                  <span className={styles.explanationTitle}>Explanation</span>
                  <span className={styles.preWrap}>{med.explanation}</span>
                </div>
              )}
            </div>
          ),
        )}
      </div>
    );
  }

  //
  //Answering
  //
  return (
    <div className={styles.block}>
      <span className={styles.instructions}>
        For each medication, select a treatment classification and a medication
        regimen.
      </span>
      <div className={styles.tables}>
        {/* Treatment classification */}
        <div className={styles.table}>
          <div className={styles.titleBar}>Treatment Classification</div>
          <div className={styles.grid}>
            <span className={styles.columnHeader}>Medication</span>
            <span className={styles.columnHeader}>
              Treatment Classification
            </span>
            {medications.map((med) => (
              <div key={med.med_id} className={styles.row}>
                <label
                  className={styles.rowLabel}
                  htmlFor={`dlosce-class-${currentQuestionId}-${med.med_id}`}
                >
                  {med.drug}
                </label>
                <select
                  id={`dlosce-class-${currentQuestionId}-${med.med_id}`}
                  className={styles.select}
                  disabled={locked}
                  value={answerFor(med.med_id)?.classification ?? ""}
                  onChange={(e) =>
                    updateAnswer(med.med_id, {
                      classification: e.target.value || null,
                    })
                  }
                >
                  <option value="" />
                  {classifications.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Medication regimen */}
        <div className={styles.table}>
          <div className={styles.titleBar}>Medication Regimen</div>
          <div className={styles.grid}>
            <span className={styles.columnHeader}>Medication</span>
            <span className={styles.columnHeader}>Medication Regimen</span>
            {medications.map((med) => (
              <div key={med.med_id} className={styles.row}>
                <label
                  className={styles.rowLabel}
                  htmlFor={`dlosce-regimen-${currentQuestionId}-${med.med_id}`}
                >
                  {med.drug}
                </label>
                <select
                  id={`dlosce-regimen-${currentQuestionId}-${med.med_id}`}
                  className={styles.select}
                  disabled={locked}
                  value={answerFor(med.med_id)?.regimen_id?.toString() ?? ""}
                  onChange={(e) =>
                    updateAnswer(med.med_id, {
                      regimen_id:
                        e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                >
                  <option value="" />
                  {med.regimens.map((r) => (
                    <option key={r.regimen_id} value={r.regimen_id}>
                      {r.display}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

//
//One drop-down shown as a stacked list of all its options:
//check on the correct option, cross on a wrong pick
//
type OptionRow = {
  key: string;
  text: string;
  isCorrect: boolean;
  isPicked: boolean;
};

function OptionList({
  title,
  answered,
  correct,
  options,
}: {
  title: string;
  answered: boolean;
  correct: boolean;
  options: OptionRow[];
}) {
  return (
    <div className={styles.optionGroup}>
      <div className={styles.optionGroupHeader}>
        <span className={styles.optionGroupTitle}>{title}</span>
        <span
          className={`${styles.status} ${
            !answered ? "" : correct ? testgreenText : testRedText
          }`}
        >
          {!answered ? "Not answred" : correct ? "Correct" : "Incorrect"}
        </span>
      </div>
      <div className={styles.optionList}>
        {options.map((option) => {
          const wrongPick = option.isPicked && !option.isCorrect;
          const colors = option.isCorrect
            ? `${testgreenBackground} ${testgreenText}`
            : wrongPick
              ? `${testRedBackground} ${testRedText}`
              : "";
          return (
            <div
              key={option.key}
              className={`${styles.option} ${colors} ${
                option.isPicked ? styles.optionPicked : ""
              }`}
            >
              <span className={styles.optionIcon}>
                {option.isCorrect && <Check size={18} aria-label="Correct" />}
                {wrongPick && <X size={18} aria-label="Incorrect" />}
              </span>
              <span className={styles.optionText}>{option.text}</span>
              {option.isPicked && (
                <span className={styles.yourAnswer}>Your answer</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DloscePrescriptionAnswerBlock;
